import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Agents
export const getAgents = () => api.get('/agents');
export const createAgent = (data) => api.post('/agents', data);
export const deleteAgent = (id) => api.delete(`/agents/${id}`);

// Categories
export const getCategories = (agentId) => 
  api.get('/categories', { params: { agent_id: agentId } });
export const createCategory = (data) => api.post('/categories', data);
export const deleteCategory = (id) => api.delete(`/categories/${id}`);

// Tasks
export const getTasks = (params) => api.get('/tasks', { params });
export const createTask = (data) => api.post('/tasks', data);
export const deleteTask = (id) => api.delete(`/tasks/${id}`);

// Prompts
export const getPrompts = (params) => api.get('/prompts', { params });
export const getPrompt = (id) => api.get(`/prompts/${id}`);
export const createPrompt = (data) => api.post('/prompts', data);
export const updatePrompt = (id, data) => api.put(`/prompts/${id}`, data);
export const deletePrompt = (id) => api.delete(`/prompts/${id}`);

// Versions
export const getPromptVersions = (promptId) => 
  api.get(`/prompts/${promptId}/versions`);
export const getPromptVersion = (promptId, versionNum) =>
  api.get(`/prompts/${promptId}/versions/${versionNum}`);
export const getVersionDiff = (promptId, versionNum) =>
  api.get(`/prompts/${promptId}/versions/${versionNum}/diff`);
export const restoreVersion = (promptId, versionNum) => 
  api.post(`/prompts/${promptId}/restore/${versionNum}`);
export const deleteVersion = (promptId, versionNum) =>
  api.delete(`/prompts/${promptId}/versions/${versionNum}`);

// Models
export const getModels = () => api.get('/models');

// Token counting
export const countTokens = (text, model = 'gpt-4o') => 
  api.post('/tokens/count', { text, model });

// Test Cases
export const getTestCases = (promptId) => 
  api.get(`/prompts/${promptId}/test-cases`);
export const createTestCase = (data) => api.post('/test-cases', data);
export const updateTestCase = (id, data) => api.put(`/test-cases/${id}`, data);
export const deleteTestCase = (id) => api.delete(`/test-cases/${id}`);

// Run Prompt
export const runPrompt = (data) => api.post('/run-prompt', data);
export const runTestCase = (testCaseId, provider, modelName, promptContent = null) => {
  let url = `/run-test-case/${testCaseId}?provider=${provider}&model_name=${modelName}`;
  if (promptContent) {
    url += `&prompt_content=${encodeURIComponent(promptContent)}`;
  }
  return api.post(url);
};
export const runTestCaseWithContent = (testCaseId, provider, modelName, promptContent) => 
  api.post(`/run-test-case/${testCaseId}?provider=${provider}&model_name=${modelName}`, null, {
    params: { prompt_content: promptContent }
  });
export const getTestResults = (promptId) => 
  api.get(`/prompts/${promptId}/test-results`);

// Seed
export const seedDatabase = () => api.post('/seed');

// Langfuse Import
export const previewLangfuseImport = (traceData) => 
  api.post('/import/langfuse/preview', { trace_data: traceData });
export const importLangfuseTrace = (traceData) => 
  api.post('/import/langfuse', { trace_data: traceData });

// Few-Shots
export const getFewShots = (params) => api.get('/few-shots', { params });
export const getFewShot = (id) => api.get(`/few-shots/${id}`);
export const createFewShot = (data) => api.post('/few-shots', data);
export const updateFewShot = (id, data) => api.put(`/few-shots/${id}`, data);
export const deleteFewShot = (id) => api.delete(`/few-shots/${id}`);
export const getFewShotVersions = (id) => api.get(`/few-shots/${id}/versions`);
export const getFewShotVersionDiff = (id, versionNum) => api.get(`/few-shots/${id}/versions/${versionNum}/diff`);
export const restoreFewShotVersion = (id, versionId) => api.post(`/few-shots/${id}/versions/${versionId}/restore`);
export const testFewShotRetrieval = (data) => api.post('/few-shots/test-retrieval', data);
export const getFewShotAnalytics = () => api.get('/few-shots/analytics');
export const bulkImportFewShots = (data) => api.post('/few-shots/bulk-import', data);
export const bulkExportFewShots = (params) => api.get('/few-shots/bulk-export', { params });
export const validateAllFewShots = () => api.post('/few-shots/validate-all');
export const validateFewShot = (id) => api.post(`/few-shots/${id}/validate`);

// Analytics Catalogue - Concepts
export const getConcepts = (params) => api.get('/concepts', { params });
export const getConcept = (id) => api.get(`/concepts/${id}`);
export const createConcept = (data) => api.post('/concepts', data);
export const updateConcept = (id, data) => api.put(`/concepts/${id}`, data);
export const deleteConcept = (id) => api.delete(`/concepts/${id}`);
export const getConceptVersions = (id) => api.get(`/concepts/${id}/versions`);
export const getConceptVersionDiff = (id, versionNum) => api.get(`/concepts/${id}/versions/${versionNum}/diff`);
export const restoreConceptVersion = (id, versionId) => api.post(`/concepts/${id}/versions/${versionId}/restore`);
export const validateConcept = (id) => api.post(`/concepts/${id}/validate`);
export const searchSimilarConcepts = (query) => api.post('/concepts/search-similar', null, { params: { query } });
export const bulkImportConcepts = (data) => api.post('/concepts/bulk-import', data);
export const bulkExportConcepts = (params) => api.get('/concepts/bulk-export', { params });

// Concept Metadata
export const getAllMetadata = () => api.get('/metadata/all');
export const getAllMetadataVersions = () => api.get('/metadata/versions/all');
export const getConceptMetadata = (conceptId) => api.get(`/concepts/${conceptId}/metadata`);
export const saveConceptMetadata = (conceptId, data) => api.post(`/concepts/${conceptId}/metadata`, data);
export const updateColumnMetadata = (conceptId, columnId, data) => api.put(`/concepts/${conceptId}/metadata/column/${columnId}`, data);
export const addColumnMetadata = (conceptId, data) => api.post(`/concepts/${conceptId}/metadata/column`, data);
export const deleteColumnMetadata = (conceptId, columnId) => api.delete(`/concepts/${conceptId}/metadata/column/${columnId}`);
export const addLink = (conceptId, data) => api.post(`/concepts/${conceptId}/metadata/link`, data);
export const updateLink = (conceptId, linkId, data) => api.put(`/concepts/${conceptId}/metadata/link/${linkId}`, data);
export const deleteLink = (conceptId, linkId) => api.delete(`/concepts/${conceptId}/metadata/link/${linkId}`);
export const getMetadataVersions = (conceptId) => api.get(`/concepts/${conceptId}/metadata/versions`);
export const restoreMetadataVersion = (conceptId, versionId) => api.post(`/concepts/${conceptId}/metadata/versions/${versionId}/restore`);
export const deleteMetadataVersion = (conceptId, versionId) => api.delete(`/concepts/${conceptId}/metadata/versions/${versionId}`);
export const regenerateGlossary = (conceptId, data) => api.post(`/concepts/${conceptId}/metadata/regenerate-glossary`, data);

// Universal Instructions
export const getUniversalInstruction = () => api.get('/universal-instructions');
export const updateUniversalInstruction = (data) => api.put('/universal-instructions', data);
export const getUniversalInstructionVersions = () => api.get('/universal-instructions/versions');
export const getUniversalInstructionDiff = (versionNum) => api.get(`/universal-instructions/versions/${versionNum}/diff`);
export const restoreUniversalInstructionVersion = (versionNum) => api.post(`/universal-instructions/versions/${versionNum}/restore`);

// Usecase Summary
export const getUsecaseSummary = () => api.get('/usecase-summary');
export const generateUsecaseSummary = () => api.post('/usecase-summary/generate');
export const updateUsecaseSummary = (data) => api.put('/usecase-summary', data);
export const getUsecaseSummaryVersions = () => api.get('/usecase-summary/versions');
export const getUsecaseSummaryDiff = (versionNum) => api.get(`/usecase-summary/versions/${versionNum}/diff`);
export const restoreUsecaseSummaryVersion = (versionNum) => api.post(`/usecase-summary/versions/${versionNum}/restore`);

export default api;
